import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type {
  EtatCommande,
  MotifCommande,
  RaisonNonVendable,
  StatutOeuvre,
} from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
export interface CommandePresentee {
  id: string;
  reference: string;
  etat: EtatCommande;
  motif: MotifCommande | null;
  oeuvre: {
    id: string;
    titre: string;
    artisteNom: string;
    mediaPrincipalId: string | null;
    apercuUrl?: string;
  };
  exemplaire: string | null;
  contact: { id: string; nomAffiche: string };
  montantCentimes: number;
  devise: 'EUR';
  expireLe: string;
  payeeLe: string | null;
  paiementId: string | null;
  facture: { id: string; numero: string | null } | null;
  avoir: { id: string; numero: string | null } | null;
  creeLe: string;
}
export interface OeuvreBoutique {
  id: string;
  titre: string;
  artisteNom: string;
  statut: StatutOeuvre;
  mediaPrincipalId: string | null;
  apercuUrl?: string;
  enVente: boolean;
  raison: RaisonNonVendable | null;
  prixTtcCentimes: number | null;
}
export interface ReglagesBoutique {
  ouverte: boolean;
  conditionsVente: { fr: string; en?: string };
  conditionsValidees: boolean;
  conditionsValideesLe: string | null;
}
export type CorpsReglagesBoutique = Partial<Omit<ReglagesBoutique, 'conditionsValideesLe'>>;
export interface FiltresCommandes {
  etat?: EtatCommande;
  page?: number;
  parPage?: number;
}
export interface FiltresOeuvresBoutique {
  q?: string;
  enVente?: boolean;
  page?: number;
  parPage?: number;
}
const chemin = (g: string, suite: string) => `/g/${encodeURIComponent(g)}/boutique/${suite}`;
function query(f: FiltresCommandes | FiltresOeuvresBoutique) {
  return new URLSearchParams(
    Object.entries({ parPage: 30, ...f }).map(([k, v]) => [k, String(v)]),
  ).toString();
}
export const boutiqueApi = {
  reglages: (g: string, a?: AppelApi) =>
    api.get<{ reglages: ReglagesBoutique }>(chemin(g, 'reglages'), { galerie: g, ...a }),
  modifierReglages: (g: string, corps: CorpsReglagesBoutique, a?: AppelApi) =>
    api.put<{ reglages: ReglagesBoutique }>(chemin(g, 'reglages'), corps, { galerie: g, ...a }),
  oeuvres: (g: string, f: FiltresOeuvresBoutique = {}, a?: AppelApi) =>
    api.get<{ oeuvres: OeuvreBoutique[]; pagination: Pagination }>(
      `${chemin(g, 'oeuvres')}?${query(f)}`,
      { galerie: g, ...a },
    ),
  mettreEnVente: (g: string, id: string, enVente: boolean, a?: AppelApi) =>
    api.put<OeuvreBoutique>(
      chemin(g, `oeuvres/${encodeURIComponent(id)}`),
      { enVente },
      { galerie: g, ...a },
    ),
  commandes: (g: string, f: FiltresCommandes = {}, a?: AppelApi) =>
    api.get<{ commandes: CommandePresentee[]; pagination: Pagination }>(
      `${chemin(g, 'commandes')}?${query(f)}`,
      { galerie: g, ...a },
    ),
  commande: (g: string, id: string, a?: AppelApi) =>
    api.get<CommandePresentee>(chemin(g, `commandes/${encodeURIComponent(id)}`), {
      galerie: g,
      ...a,
    }),
  rembourser: (g: string, id: string, motif: string, a?: AppelApi) =>
    api.post<CommandePresentee>(
      chemin(g, `commandes/${encodeURIComponent(id)}/rembourser`),
      { motif },
      { galerie: g, ...a },
    ),
};
export function useReglagesBoutique(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'boutique', 'reglages'),
    queryFn: (c) => boutiqueApi.reglages(g, appelDeRequete(c)),
    select: (r) => r.reglages,
  });
}
export function useOeuvresBoutique(g: string, f: FiltresOeuvresBoutique = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'boutique', 'oeuvres', f),
    queryFn: (c) => boutiqueApi.oeuvres(g, f, appelDeRequete(c)),
  });
}
export function useCommandesBoutique(g: string, f: FiltresCommandes = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'boutique', 'commandes', f),
    queryFn: (c) => boutiqueApi.commandes(g, f, appelDeRequete(c)),
  });
}
export function useCommandeBoutique(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'boutique', 'commande', id),
    queryFn: (c) => boutiqueApi.commande(g, id, appelDeRequete(c)),
    enabled: !!id,
  });
}
function useMutationBoutique<T, R>(g: string, fn: (v: T, a: AppelApi) => Promise<R>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      gcTime: 0,
      mutationFn: fn,
      onSuccess: () => {
        for (const collection of [
          'boutique',
          'oeuvres',
          'ventes',
          'reservations',
          'tableau-de-bord',
          'site',
        ])
          void client.invalidateQueries({ queryKey: cleMetier(g, collection) });
      },
    }),
  );
}
export function useModifierReglagesBoutique(g: string) {
  return useMutationBoutique(g, (v: CorpsReglagesBoutique, a) =>
    boutiqueApi.modifierReglages(g, v, a),
  );
}
export function useMettreEnVente(g: string) {
  return useMutationBoutique(g, (v: { id: string; enVente: boolean }, a) =>
    boutiqueApi.mettreEnVente(g, v.id, v.enVente, a),
  );
}
export function useRembourserCommande(g: string) {
  return useMutationBoutique(g, (v: { id: string; motif: string }, a) =>
    boutiqueApi.rembourser(g, v.id, v.motif, a),
  );
}
